import { useNavigate } from 'react-router';
import { Store } from 'lucide-react';
import AlertBanner from '@common/feedback/AlertBanner';
import { PATHS } from '@/navigation/paths';
import { useAnnouncement } from './useAnnouncement';

/**
 * BecomeVendorBanner — the dashboard announcement that store applications
 * are open to everyone, with a way into the application and a way to close
 * it for good.
 *
 * WHO SEES IT is the backend's decision, not this component's. The read
 * returns only the announcements this user should be shown right now (not a
 * vendor, never dismissed it), so there is no role check here: a rule kept
 * in two places drifts. The component renders what it is given. The one
 * moment the answer changes without a dismissal is the application itself,
 * and useBecomeVendorSubmission reads this list again right after it.
 *
 * DISMISSED FOREVER means on the server (AnnouncementDismissal), so it
 * holds on a new device and after a cleared browser. The dismissal's own
 * answer is the new list and the slice writes it into the read's cache, so
 * the banner leaves with no reload and no second request. A dismissal that
 * fails changes nothing: the banner stays and the person is told
 * (useAnnouncement, shared with the listings-need-receipts notice).
 *
 * Web only (Tyler, 2026-09-21): store applications are web only, so the
 * mobile app has no such banner.
 */

/** The banner's wording, approved by Tyler 2026-09-21. Tests read labels from here. */
export const BECOME_VENDOR_BANNER_COPY = {
  title: 'Vendor Applications Are Now Open!',
  message: 'Start listing your gear today!',
  action: 'Become a vendor',
  dismiss: 'Dismiss',
} as const;

export function BecomeVendorBanner() {
  const navigate = useNavigate();
  const { shown, dismiss } = useAnnouncement('become-vendor-open');
  if (!shown) return null;

  return (
    <AlertBanner
      variant='notice'
      role='status'
      testID='become-vendor-banner'
      icon={<Store size={18} />}
      title={BECOME_VENDOR_BANNER_COPY.title}
      actionLabel={BECOME_VENDOR_BANNER_COPY.action}
      onAction={() => navigate(PATHS.dashboard.becomeVendor)}
      dismissLabel={BECOME_VENDOR_BANNER_COPY.dismiss}
      onDismiss={() => void dismiss()}
    >
      {BECOME_VENDOR_BANNER_COPY.message}
    </AlertBanner>
  );
}
